// Current-value formatter for Signaalit, equipment cards, and zone tiles.
// Binary status is stored as 1.0/0.0 with unit on/off (KB dialect ON/OFF);
// showing "1.0 on/off" is not an enum — map it to ON/OFF and drop the unit.

export const BINARY_ON_LABEL = "ON";
export const BINARY_OFF_LABEL = "OFF";

export interface SignalValueInput {
  numeric_value?: number | null;
  text_value?: string | null;
  unit?: string | null;
  nature?: string | null;
}

function normalized_unit(unit: string | null | undefined): string {
  return (unit ?? "").trim().toLowerCase().replace(/[_-]/g, "/");
}

export function isBinarySignalUnit(unit: string | null | undefined): boolean {
  return normalized_unit(unit) === "on/off";
}

// Headcount is a discrete quantity (KB unit `persons`). One decimal is a
// continuous-sensor default and would invent half-people.
export function isCountSignalUnit(unit: string | null | undefined): boolean {
  return normalized_unit(unit) === "persons";
}

function is_binary_signal(
  unit: string | null | undefined,
  nature: string | null | undefined,
): boolean {
  return isBinarySignalUnit(unit) || nature === "status";
}

function format_numeric(
  value: number,
  unit: string | null | undefined,
): string {
  const body = isCountSignalUnit(unit)
    ? String(Math.round(value))
    : value.toFixed(1);
  return unit ? `${body} ${unit}` : body;
}

export function formatSignalNumericValue(
  value: number,
  unit?: string | null,
  nature?: string | null,
): string {
  if (is_binary_signal(unit, nature)) {
    return value !== 0 ? BINARY_ON_LABEL : BINARY_OFF_LABEL;
  }
  return format_numeric(value, unit);
}

export function formatSignalValue(
  sample: SignalValueInput | undefined,
  unit_override?: string | null,
): string {
  if (!sample) return "–";
  const unit = unit_override ?? sample.unit ?? null;
  if (sample.numeric_value != null) {
    return formatSignalNumericValue(sample.numeric_value, unit, sample.nature);
  }
  if (sample.text_value != null && sample.text_value !== "") {
    return sample.text_value;
  }
  return "–";
}
